import { Accordion, Box, Button, Flex, Group, MantineColor, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { memo, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import {
  useUndoAllMutation,
  useUpsertCategoryDetections,
} from 'src/data-capture/detected-entities/hooks/DetectionActionMutations';
import {
  AfternoonAmountTooLargeApiError,
  AfternoonFieldSizeExceededApiError,
} from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import {
  EntityCategory,
  selectCategoryStateCounts,
  selectDetections,
  selectEntityTypes,
  selectHasActioning,
  selectNewPayloadForCategory,
  selectValidationErrorsInCategory,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import EntityTypeAccordionItem from './EntityTypeAccordionItem';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ValidationErrorsModal } from './ValidationErrorsModal';

type Props = {
  category: EntityCategory;
  bottomBorder?: boolean;
};

function CategoryEntities({ category, bottomBorder }: Props) {
  const [activeEntityTypes, setActiveEntityTypes] = useState<string[]>([]);
  const entityTypes = useDetectedEntitiesStore(state => selectEntityTypes(state, category));
  const { showDataUploadCounts } = useFlags();

  return (
    <Flex
      direction="column"
      gap="md"
      py="xl"
      style={{
        borderBottom: bottomBorder ? '1px solid var(--mantine-color-gray-3)' : 'none',
      }}
      data-testid={`category-entities-${category}`}
    >
      <Flex justify="space-between" align="center" style={{ width: '100%' }}>
        <Flex gap="xs" align="center">
          <>
            <CategoryDot category={category} />
            <Text fw={600} size="lg">
              {getTitle(category, entityTypes.length, showDataUploadCounts)}
            </Text>
          </>
        </Flex>
        {entityTypes.length > 0 && <ImportAllButton category={category} />}
      </Flex>
      <Accordion
        multiple
        variant="document-entity"
        value={activeEntityTypes}
        onChange={setActiveEntityTypes}
      >
        <Flex direction="column" gap="xl">
          {entityTypes.map(({ entityType }, index) => (
            <EntityTypeAccordionItem
              key={`${entityType}-${index}`}
              entityType={entityType}
              category={category}
              isActive={activeEntityTypes.includes(entityType)}
            />
          ))}
        </Flex>
      </Accordion>
    </Flex>
  );
}

const CategoryDot = memo(function CategoryDot({ category }: { category: EntityCategory }) {
  let backgroundColor: MantineColor;
  switch (category) {
    case 'new':
      backgroundColor = 'success.7';
      break;
    case 'info-update':
      backgroundColor = 'warning.5';
      break;
    case 'conflicts':
      backgroundColor = 'error.7';
      break;
  }

  return <Box w={24} h={24} bg={backgroundColor} style={{ borderRadius: 12 }} />;
});

const ImportAllButton = memo(function ImportAllButton({ category }: { category: EntityCategory }) {
  const upsertPayload = useDetectedEntitiesStore(state =>
    selectNewPayloadForCategory(state, category),
  );
  const stateCounts = useDetectedEntitiesStore(state => selectCategoryStateCounts(state, category));
  const validationErrors = useDetectedEntitiesStore(state =>
    selectValidationErrorsInCategory(state, category),
  );
  const { clientId } = useCurrentClient();
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const { upsertCategoryDetections } = useUpsertCategoryDetections(category);
  const hasActioning = useDetectedEntitiesStore(state => selectHasActioning(state, category));
  const detectionsInCategory = useDetectedEntitiesStore(state => selectDetections(state, category));

  const cta = category === 'info-update' ? 'Update all with new' : 'Add all as new';
  async function handleImportAll() {
    if (validationErrors.length > 0) {
      setShowValidationErrors(true);
      return;
    }

    try {
      await upsertCategoryDetections(clientId);
    } catch (error) {
      if (AfternoonFieldSizeExceededApiError.isFieldSizeExceededError(error)) {
        const entityLabel = detectionsInCategory
          .filter(d => error.field in d.fieldsByKey)
          .sort((a, b) => {
            const aLen = String(a.fieldsByKey[error.field]?.value ?? '').length;
            const bLen = String(b.fieldsByKey[error.field]?.value ?? '').length;
            return bLen - aLen;
          })[0]?.entityLabel;
        Logger.error(
          `Field size exceeded error upserting entities for category ${category}`,
          error,
          {
            field: error.field,
          },
        );
        notifications.show({
          color: 'red',
          title: 'Value too long',
          message: entityLabel ? `${entityLabel} — ${error.message}` : error.message,
        });
      } else if (AfternoonAmountTooLargeApiError.isAmountTooLargeError(error)) {
        const entityLabel = detectionsInCategory
          .filter(d => error.field in d.fieldsByKey)
          .sort((a, b) => {
            const aVal = Number(a.fieldsByKey[error.field]?.value ?? 0);
            const bVal = Number(b.fieldsByKey[error.field]?.value ?? 0);
            return bVal - aVal;
          })[0]?.entityLabel;
        notifications.show({
          color: 'red',
          title: 'Amount too large',
          message: entityLabel ? `${entityLabel} — ${error.message}` : error.message,
        });
      } else {
        Logger.error(`Failed upserting all entities for category ${category}`, error);
        notifications.show({
          color: 'red',
          title: 'Action failed',
          message: 'Something went wrong, please try again.',
        });
      }
    }
  }

  const enableUndo = stateCounts.active === 0 && stateCounts.discarded + stateCounts.resolved > 0;

  return (
    <Group>
      {enableUndo ? (
        <UndoAll category={category} />
      ) : (
        <Button
          variant="primary"
          size="xs"
          onClick={handleImportAll}
          disabled={upsertPayload.length === 0}
          loading={hasActioning}
          data-testid="import-all-button"
        >
          {cta}
        </Button>
      )}
      <ValidationErrorsModal
        opened={showValidationErrors}
        close={() => setShowValidationErrors(false)}
        validationErrors={validationErrors}
      />
    </Group>
  );
});

function UndoAll({ category }: { category: EntityCategory }) {
  const { undoAllInCategory } = useUndoAllMutation();
  const { clientId } = useCurrentClient();

  async function handleUndoAll() {
    await undoAllInCategory(clientId, category);
  }

  return (
    <Button variant="text" size="xs" onClick={handleUndoAll}>
      Undo all
    </Button>
  );
}

function getTitle(category: EntityCategory, numTypes: number, showDataUploadCounts: boolean) {
  if (showDataUploadCounts) {
    switch (category) {
      case 'new':
        return `${numTypes} new data ${numTypes === 1 ? 'item' : 'items'}`;
      case 'info-update':
        return `${numTypes} information ${numTypes === 1 ? 'update' : 'updates'} for review`;
      case 'conflicts':
        return `${numTypes} possible ${numTypes === 1 ? 'conflict' : 'conflicts'}, please choose`;
    }
  }

  switch (category) {
    case 'new':
      return 'New data items';
    case 'info-update':
      return 'Information updates for review';
    case 'conflicts':
      return 'Possible conflicts, please choose';
  }
}

export default memo(CategoryEntities);
